import { useEffect, useState } from "react";
import { check, close } from "../../asset";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import ToggleSwitch from "../../components/ToggleSwitch";
import Tag from "../../components/Tags";

type AddEditQuestionsProps = {
  onSave: (data: any) => void | Promise<void>;
  onClose: () => void;
  editingData?: any;
  mode?: "FORM" | "FIELD";
};

export default function AddEditQuestions({
  onSave,
  editingData,
  onClose,
  mode = "FORM",
}: AddEditQuestionsProps) {
  const [title, setTitle] = useState(editingData?.fieldName || "");
  const [fieldDescription, setFieldDescription] = useState(
    editingData?.fieldDescription || "",
  );
  const [fieldAbbr, setFieldAbbr] = useState(editingData?.fieldAbbr || "");

  const [type, setType] = useState(editingData?.fieldType || "");
  const [required, setRequired] = useState(
    editingData?.isFieldRequired || false,
  );
  const [isSelected, setIsSelected] = useState(editingData?.isSelected ?? true);

  const [choices, setChoices] = useState(editingData?.options || []);
  const [showChoiceInput, setShowChoiceInput] = useState(false);
  const [currentChoice, setCurrentChoice] = useState("");

  const toFieldType = (value: string) =>
    value
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/\//g, "_")
      .replace(/[()]/g, "");

  const handleSubmit = () => {
    if (mode === "FIELD") {
      const payload = {
        ...(editingData ?? {}),
        fieldName: title,
        fieldDescription,
        fieldAbbr,
        fieldType: toFieldType(type),
        options: choices,
        isFieldRequired: required,
        isSelected,
        isPreAdded: false,
        parentId: 1,
        schoolBranchCode: editingData?.schoolBranchCode,
      };

      onSave(payload);
    } else {
      const normalized = {
        title,
        type: toFieldType(type),
        required,
        choices,
      };

      onSave(normalized);
    }

    onClose();
  };

  const addChoice = () => {
    if (!currentChoice.trim()) return;
    setChoices([...choices, currentChoice.trim()]);
    setCurrentChoice("");
    setShowChoiceInput(false);
  };

  useEffect(() => {
    if (!editingData) {
      // reset for add mode
      setTitle("");
      setFieldDescription("");
      setFieldAbbr("");
      setType("");
      setRequired(false);
      setIsSelected(true);
      setChoices([]);
      return;
    }

    // prefill for edit mode
    setTitle(editingData.fieldName || editingData.title || "");
    setFieldDescription(editingData.fieldDescription || "");
    setFieldAbbr(editingData.fieldAbbr || "");
    setType(editingData.fieldType || editingData.type || "");
    setRequired(editingData.isFieldRequired ?? editingData.required ?? false);
    setIsSelected(editingData.isSelected ?? true);
    setChoices(editingData.options || editingData.choices || []);
  }, [editingData]);

  const removeChoice = (idx) => {
    setChoices(choices.filter((_, i) => i !== idx));
  };

  return (
    <div className="max-w-3xl flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {mode === "FIELD" && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => setIsSelected((prev) => !prev)}
            />
          )}

          <h3 className="text-base font-semibold text-neutral-400">
            {editingData ? "Edit Question" : "Add Question"}
          </h3>
        </div>

        <div className="flex items-center gap-4 text-sm font-bold text-neutral-400">
          <label>Required?</label>
          <ToggleSwitch
            defaultChecked={required}
            onToggle={() => setRequired(!required)}
          />
        </div>
      </div>

      <CustomSelect
        name="fieldType"
        label="Question answer type"
        placeholder={editingData?.fieldType || "Select Field Type"}
        value={type}
        onChange={(e) => setType(e.target.value)}
        options={[
          "Single Line Answer",
          "Mulit Line Answer",
          "NUMBER",
          "Yes NO",
          "Single Seclection From List of option",
          "Multi Seclection From List of option",
          "Date",
          "Yes or NO (with additional text if yes)",
          "Yes or NO (with additional text if NO)",
          "Email",
          "Phone/Mobile Number",
          "Simple Check Box",
          "Profile Picture Upload",
          "Documents Upload",
        ]}
      />

      <CustomInput
        name="fieldName"
        label="Field Name"
        placeholder="Enter field name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      {mode === "FIELD" && (
        <>
          <CustomInput
            name="fieldDescription"
            label="Field Description"
            value={fieldDescription}
            onChange={(e) => setFieldDescription(e.target.value)}
          />

          <CustomInput
            name="fieldAbbr"
            label="Field Abbreviation (abbr)"
            value={fieldAbbr}
            onChange={(e) => setFieldAbbr(e.target.value)}
          />
        </>
      )}

      {(type === "DROPDOWN" ||
        type === "Single Seclection From List of option" ||
        type === "Multi Seclection From List of option" ||
        type === "Simple Check Box") && (
        <div className="flex gap-4 items-center">
          <div className="flex gap-2">
            <label className="text-neutral-400 font-semibold text-sm">
              Choices
            </label>

            <div className="flex flex-wrap gap-2">
              {choices.map((choice, idx) => (
                <Tag
                  key={idx}
                  text={choice}
                  color="primary"
                  onRemove={() => removeChoice(idx)}
                />
              ))}
            </div>
          </div>

          {!showChoiceInput ? (
            <button
              className="bg-success-50 text-xs font-medium text-nowrap text-success-600 px-2 py-1 rounded w-fit"
              onClick={() => setShowChoiceInput(true)}
            >
              Add +
            </button>
          ) : (
            <div className="flex gap-2 w-full">
              <CustomInput
                name="newChoice"
                placeholder="Enter here"
                value={currentChoice}
                onChange={(e) => setCurrentChoice(e.target.value)}
              />

              <button
                onClick={addChoice}
                className="bg-success-500 px-4 py-2 rounded-lg"
              >
                <img className="w-7 h-7" src={check} alt="" />
              </button>

              <button
                onClick={() => {
                  setCurrentChoice("");
                  setShowChoiceInput(false);
                }}
                className="bg-error-500 px-4 py-2 rounded-lg"
              >
                <img className="w-7 h-7" src={close} alt="" />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 text-xs">
        <button
          onClick={handleSubmit}
          className="bg-primary-500 rounded-full px-4 py-2 text-white"
        >
          {editingData ? "Update" : "Save"}
        </button>

        <button
          onClick={onClose}
          className="bg-neutral-200 text-white rounded-full px-4 py-2"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
